(() => {
  if (window.NubsSoftNavigation?.initialized) return;

  const DOCUMENT_CACHE_TTL_MS = 2 * 60 * 1000;
  const DOCUMENT_CACHE_LIMIT = 12;
  const SAFE_PAGE_PATHS = new Set([
    '/pages/my-collection',
    '/pages/account',
    '/pages/forum',
    '/pages/messages',
    '/pages/profile',
    '/pages/database-history',
    '/pages/faq',
    '/pages/accessibility',
    '/pages/database-guidelines',
    '/pages/change-requests',
    '/pages/new-entry-requests',
  ]);

  const documentCache = new Map();
  const shopViews = new Map();
  const pendingStyles = new Map();
  const secondaryScripts = new Set(['nubs-recommendations.js', 'nubs-recently-viewed.js']);
  // Only styles owned by the theme can be switched on a route change.
  const routeStyles = new Set([
    'home-page.css', 'product-page.css', 'collection-page.css', 'browse-directory.css',
    'search-page.css', 'page.css', 'cart-page.css', '404-page.css', 'list-collections-page.css',
    'contact-page.css', 'nubs-add-database-entry.css', 'nubs-request-a-change.css',
    'nubs-change-requests.css', 'nubs-new-entry-requests.css', 'nubs-admin-nav.css',
    'nubs-admin-home.css', 'nubs-admin-catalog.css', 'nubs-admin-change-requests.css',
    'nubs-admin-new-entries.css', 'nubs-account.css', 'nubs-community.css', 'nubs-profile.css',
    'nubs-media-release.css', 'nubs-database-history.css',
  ]);
  let activeNavigationController = null;
  let navigationSerial = 0;
  let renderedUrl = window.location.href;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function canonicalUrl(input) {
    const url = new URL(input, window.location.href);
    url.hash = '';
    return url.toString();
  }

  function localeNeutralPath(pathname) {
    const routeRoot = String(window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    if (!routeRoot) return pathname || '/';
    if (pathname === routeRoot) return '/';
    if (pathname.startsWith(`${routeRoot}/`)) return pathname.slice(routeRoot.length) || '/';
    return pathname || '/';
  }

  function isReleasePath(pathname) {
    return localeNeutralPath(pathname).startsWith('/community/release/');
  }

  function isSoftNavigationUrl(input) {
    let url;
    try {
      url = new URL(input, window.location.href);
    } catch (_) {
      return false;
    }

    if (url.origin !== window.location.origin) return false;
    const locale = path => /^\/(fr|es)(?:\/|$)/.exec(path)?.[1] || 'en';
    if (locale(url.pathname) !== locale(location.pathname)) return false;
    if (!['http:', 'https:'].includes(url.protocol)) return false;
    if (['fuzzy-index','nubs-recommendations','nubs-sold-out'].includes(url.searchParams.get('view'))) return false;
    const path=localeNeutralPath(url.pathname);
    if (path==='/' || path==='/search' || /^\/(products|collections)\/[^/.]+$/.test(path)) return true;
    if (SAFE_PAGE_PATHS.has(path)) return true;
    if (isReleasePath(url.pathname)) return true;
    return false;
  }

  function eligibleAnchor(anchor, event) {
    if (event?.defaultPrevented) return false;
    if (!anchor?.href || !isSoftNavigationUrl(anchor.href)) return false;
    if (anchor.hasAttribute('download')) return false;
    if (anchor.dataset.nubsHardNavigation != null) return false;
    if (anchor.target && anchor.target.toLowerCase() !== '_self') return false;
    if (anchor.getAttribute('rel')?.split(/\s+/).includes('external')) return false;
    if (event && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return false;

    const target = new URL(anchor.href, window.location.href);
    const current = new URL(window.location.href);
    if (target.pathname === current.pathname && target.search === current.search && target.hash) return false;
    if (target.href === current.href) return false;
    return true;
  }

  function trimDocumentCache() {
    while (documentCache.size > DOCUMENT_CACHE_LIMIT) {
      const first = documentCache.keys().next().value;
      documentCache.delete(first);
    }
  }

  function fetchDocument(input, {signal, prefetch = false} = {}) {
    const key = canonicalUrl(input);
    const now = Date.now();
    const cached = documentCache.get(key);

    const ttl=/\/(products|collections)\//.test(new URL(key).pathname) ? 10000 : DOCUMENT_CACHE_TTL_MS;
    if (cached && (cached.complete || !cached.signal?.aborted) && now - cached.createdAt < ttl) {
      return cached.promise;
    }
    if (cached) documentCache.delete(key);

    const promise = fetch(key, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'default',
      signal,
      headers: {
        Accept: 'text/html,application/xhtml+xml',
        'X-Nubs-Soft-Navigation': prefetch ? 'prefetch' : 'navigate',
      },
    }).then(async (response) => {
      if (!response.ok) throw new Error(`Page request returned HTTP ${response.status}.`);
      const contentType = String(response.headers.get('content-type') || '');
      if (!contentType.includes('text/html')) throw new Error('Page request did not return HTML.');
      const html = await response.text();
      const entry = documentCache.get(key);
      if (entry?.promise === promise) entry.complete = true;
      return {html, responseUrl: response.url || key};
    }).catch((error) => {
      const entry = documentCache.get(key);
      if (entry?.promise === promise) documentCache.delete(key);
      throw error;
    });

    documentCache.set(key, {createdAt: now, promise, signal});
    trimDocumentCache();
    return promise;
  }

  function prefetch(input) {
    if (!isSoftNavigationUrl(input)) return;
    fetchDocument(input, {prefetch: true}).catch(() => undefined);
  }

  function stylesheetKey(href) {
    try { return new URL(href, window.location.href).toString(); }
    catch (_) { return String(href || ''); }
  }

  function waitForAsset(node, parent, {removeAfter = false} = {}) {
    return new Promise((resolve, reject) => {
      const finish = error => {
        clearTimeout(timer);
        node.removeEventListener('load', loaded);
        node.removeEventListener('error', failed);
        if (removeAfter || error) node.remove();
        if (error) reject(error); else resolve();
      };
      const loaded = () => finish();
      const failed = () => finish(new Error('Destination asset failed to load.'));
      const timer = setTimeout(failed, 3500);
      node.addEventListener('load', loaded, {once: true});
      node.addEventListener('error', failed, {once: true});
      parent.appendChild(node);
    });
  }

  function assetName(url) {
    return new URL(url, location.href).pathname.split('/').pop();
  }

  function setRouteStyles(nextDocument) {
    const desired = new Map([...nextDocument.querySelectorAll('link[rel="stylesheet"][href]')]
      .map(link => [stylesheetKey(link.href), link.getAttribute('media') || 'all']));
    document.head.querySelectorAll('link[rel="stylesheet"][href]').forEach(link => {
      if (!routeStyles.has(assetName(link.href))) return;
      link.media = desired.get(stylesheetKey(link.href)) || 'not all';
    });
  }

  async function ensureDestinationStyles(nextDocument) {
    const existing = new Set([...document.querySelectorAll('link[rel="stylesheet"][href]')]
      .map(link => stylesheetKey(link.href)));
    await Promise.all([...nextDocument.querySelectorAll('link[rel="stylesheet"][href]')].map(source => {
      const key = stylesheetKey(source.href);
      if (pendingStyles.has(key)) return pendingStyles.get(key);
      if (existing.has(key)) return;
      const link = document.createElement('link');
      for (const attribute of source.attributes) link.setAttribute(attribute.name, attribute.value);
      if (routeStyles.has(assetName(link.href))) link.media = 'not all';
      const work = waitForAsset(link, document.head).finally(() => pendingStyles.delete(key));
      pendingStyles.set(key, work);
      return work;
    }));
  }

  function scriptDescriptor(source) {
    if (!source?.src) return null;
    return {
      src: source.src,
      attributes: [...source.attributes]
        .filter((attribute) => !['src', 'defer', 'async'].includes(attribute.name.toLowerCase()))
        .map((attribute) => [attribute.name, attribute.value]),
    };
  }

  function extractPageScripts(container) {
    if (!container) return [];
    const scripts = [];
    container.querySelectorAll('script[src]').forEach((source) => {
      const descriptor = scriptDescriptor(source);
      if (descriptor) scripts.push(descriptor);
      source.remove();
    });
    return scripts;
  }

  function executeScript(descriptor) {
    const name = new URL(descriptor.src,location.href).pathname.split('/').pop();
    if (name === 'nubs-collection.js' && window.NubsCollection?.initialized) return window.NubsCollection.refresh();
    const initialize = window.NubsPageInitializers?.[name];
    if (initialize) { initialize(); return Promise.resolve(); }
    const script = document.createElement('script');
    script.src = descriptor.src;
    script.async = false;
    for (const [name, value] of descriptor.attributes) script.setAttribute(name, value);
    return waitForAsset(script, document.body, {removeAfter: true});
  }

  async function executeScripts(descriptors, serial) {
    for (const descriptor of descriptors) {
      if (serial !== navigationSerial) return;
      await executeScript(descriptor);
    }
  }


  async function warmDestinationScripts(descriptors) {
    const unique = [...new Map(
      descriptors
.filter((descriptor) => descriptor?.src && !window.NubsPageInitializers?.[new URL(descriptor.src,location.href).pathname.split('/').pop()])
        .map((descriptor) => [stylesheetKey(descriptor.src), descriptor])
    ).values()];

    await Promise.all(unique.map(descriptor => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'script';
      link.href = descriptor.src;
      link.dataset.nubsSoftNavPreload = '';
      return waitForAsset(link, document.head, {removeAfter: true});
    }));
  }

  let currentPageScripts = [...document.querySelectorAll('#MainContent script[src], #NubsPageExtras script[src]')]
    .map(scriptDescriptor).filter(Boolean);
  let currentRouteStyles = [...document.head.querySelectorAll('link[rel="stylesheet"][href]')]
    .map(link => link.cloneNode());

  function rememberShopView() {
    const url = new URL(renderedUrl);
    const path = localeNeutralPath(url.pathname);
    if (!(path === '/search' || /^\/collections\/[^/]+$/.test(path))) return;
    const main = document.getElementById('MainContent');
    if (!main?.querySelector('[data-nubs-search], [data-nubs-collection]') ||
        main.querySelector('[data-nubs-fuzzy-fallback], [data-nubs-quick-remote], [data-nubs-sold-out-pending], [aria-busy="true"]') ||
        /release-date-/.test(url.searchParams.get('nubs_sort') || url.searchParams.get('sort_by') || '')) return;

    const key = canonicalUrl(url);
    const snapshot = document.implementation.createHTMLDocument(document.title);
    for (const link of currentRouteStyles) snapshot.head.append(link.cloneNode());
    document.head.querySelectorAll('[data-nubs-seo]').forEach(node => snapshot.head.append(node.cloneNode(true)));
    const search = document.querySelector('[data-site-search]');
    if (search) snapshot.body.append(search.cloneNode(false));
    const nav = document.querySelector('.site-header .site-nav');
    if (nav) {
      const header = snapshot.createElement('header');
      header.className = 'site-header';
      header.append(nav.cloneNode(true));
      snapshot.body.append(header);
    }
    const savedMain = main.cloneNode(true);
    const savedQuery = savedMain.querySelector('[data-nubs-store-query] input[name="q"]');
    if (savedQuery) savedQuery.value = savedQuery.defaultValue;
    savedMain.removeAttribute('aria-busy');
    const selectedOptions = [...main.querySelectorAll('option')].map(option => option.selected);
    savedMain.querySelectorAll('option').forEach((option, index) =>
      option.toggleAttribute('selected', selectedOptions[index]));
    savedMain.querySelectorAll('[data-nubs-quick-ready]').forEach(node =>
      node.removeAttribute('data-nubs-quick-ready'));
    savedMain.querySelectorAll('[data-nubs-async-filters-bound]').forEach(node => node.removeAttribute('data-nubs-async-filters-bound'));
    savedMain.querySelectorAll('script[src]').forEach(node => node.remove());
    snapshot.body.append(savedMain);

    const extras = document.getElementById('NubsPageExtras')?.cloneNode(true);
    if (extras) {
      extras.querySelectorAll('script[src]').forEach(node => node.remove());
      const bar = extras.querySelector('[data-nubs-recently-viewed]');
      if (bar) bar.hidden = true;
      extras.querySelector('[data-nubs-recently-viewed-items]')?.replaceChildren();
      snapshot.body.append(extras);
    }
    const scroller = pageScrollElement();
    shopViews.delete(key);
    shopViews.set(key, {document: snapshot, scripts: currentPageScripts,
      scroll: {x: scroller.scrollLeft, y: scroller.scrollTop}, savedAt: Date.now()});
    while (shopViews.size > 2) shopViews.delete(shopViews.keys().next().value);
  }

  function takeShopView(target, historyMode) {
    if (historyMode !== 'none') return null;
    const key = canonicalUrl(target);
    const saved = shopViews.get(key);
    shopViews.delete(key);
    return saved && Date.now() - saved.savedAt < 30000 ? saved : null;
  }

  function settleLayout() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }

  function headPrewarmScripts(nextDocument) {
    return [...nextDocument.querySelectorAll('head script[data-nubs-catalog-prewarm][src]')]
      .map(scriptDescriptor)
      .filter(Boolean);
  }

  function destinationSurface(nextDocument) {
    return nextDocument.querySelector('[data-site-search]')?.dataset.defaultSearchMode === 'store'
      ? 'store'
      : 'database';
  }

  function currentSurface() {
    return document.querySelector('[data-site-search]')?.dataset.defaultSearchMode === 'store'
      ? 'store'
      : 'database';
  }

  function syncHeaderSurface(nextDocument) {
    const nextSearch = nextDocument.querySelector('[data-site-search]');
    const currentSearch = document.querySelector('[data-site-search]');
    const nextSurface = destinationSurface(nextDocument);
    const previousSurface = currentSurface();

    if (nextSearch && currentSearch) {
      currentSearch.dataset.defaultSearchMode = nextSearch.dataset.defaultSearchMode || (nextSurface === 'store' ? 'store' : 'database');
    }

    if (nextSurface !== previousSurface) {
      const currentNav = document.querySelector('.site-header .site-nav');
      const nextNav = nextDocument.querySelector('.site-header .site-nav');
      if (currentNav && nextNav) currentNav.replaceWith(document.importNode(nextNav, true));
    }

    document.dispatchEvent(new CustomEvent('nubs:surfacechange', {
      detail: {surface: nextSurface, previousSurface},
    }));
  }

  function syncDocumentMetadata(nextDocument, destinationUrl) {
    // Shopify's app-proxy head can still contain a generic title. A *fetched*
    // destination already contains its rendered release markup, so read and
    // validate the exact NMREL/title before replacing the current page.
    let validatedReleaseTitle = '';
    try {
      const destination = new URL(destinationUrl, location.href);
      const pathId = /-nmrel-(\d{6,12})$/i.exec(destination.pathname)?.[1];
      const actualId = pathId || destination.searchParams.get('nmrel');
      const release = nextDocument.querySelector('.nubs-media-release[data-current-catalog-id][data-nubs-release-public-url]');
      const catalogId = /^NMREL-(\d{6,12})$/i.exec(release?.dataset.currentCatalogId || '');
      const publicUrl = new URL(release?.dataset.nubsReleasePublicUrl || '', destination.origin);
      const renderedTitle = release?.querySelector('.nubs-media-release__title')?.textContent?.replace(/\s+/g, ' ').trim() || '';
      if (/^\d{6,12}$/.test(actualId || '') && catalogId?.[1] === actualId &&
          publicUrl.origin === destination.origin && publicUrl.pathname.endsWith(`-nmrel-${actualId}`) &&
          !publicUrl.search &&
          renderedTitle && renderedTitle.length <= 240 &&
          /^\/(?:fr\/|es\/)?community\/release\/[a-z0-9-]+$/i.test(destination.pathname)) {
        validatedReleaseTitle = renderedTitle;
        const cachePath = publicUrl.pathname.replace(/^\/(?:fr|es)(?=\/)/i, '');
        sessionStorage.setItem(`nubs:verified-release-title:${actualId}`, JSON.stringify({
          path: cachePath, title: renderedTitle, time: Date.now(),
        }));
      }
    } catch (_) { /* Normal page navigation is unaffected. */ }
    if (validatedReleaseTitle) document.title = `${validatedReleaseTitle} | Nub's Media Database`;
    else if (nextDocument.title) document.title = nextDocument.title;

    // Replace only theme-owned SEO nodes. A complete replacement also removes
    // stale robots rules, product offers, images and JSON-LD on nonproduct pages.
    // Rebuild from a narrow attribute allowlist: never execute a destination
    // script or copy event handlers, external script URLs or arbitrary markup.
    const metadata=document.createDocumentFragment();
    for(const source of nextDocument.head.querySelectorAll('[data-nubs-seo]')){
      let node=null;
      if(source.tagName==='META'){
        const name=source.getAttribute('name');
        const property=source.getAttribute('property');
        if(name && /^(?:description|robots|twitter:[a-z0-9:_-]+)$/i.test(name)){
          node=document.createElement('meta');node.setAttribute('name',name);
        }else if(property && /^(?:og|product):[a-z0-9:_-]+$/i.test(property)){
          node=document.createElement('meta');node.setAttribute('property',property);
        }
        if(node)node.setAttribute('content',source.getAttribute('content')||'');
      }else if(source.tagName==='LINK' && source.getAttribute('rel')==='canonical'){
        try{
          const href=source.getAttribute('href');
          if(!href)continue;
          const target=new URL(href,destinationUrl);
          const destination=new URL(destinationUrl,location.href);
          if((target.origin===destination.origin || target.origin==='https://nubsmedia.ca') && ['https:','http:'].includes(target.protocol)){
            node=document.createElement('link');node.setAttribute('rel','canonical');node.setAttribute('href',target.href);
          }
        }catch(_){ /* An invalid canonical is omitted, never guessed. */ }
      }else if(source.tagName==='SCRIPT' && source.getAttribute('type')==='application/ld+json'){
        try{
          const text=source.textContent||'';
          const data=JSON.parse(text);
          if(data && typeof data==='object'){
            node=document.createElement('script');node.setAttribute('type','application/ld+json');node.textContent=text;
          }
        }catch(_){ /* Invalid structured data must not survive from the old page. */ }
      }
      if(node){node.setAttribute('data-nubs-seo','');metadata.append(node);}
    }
    document.head.querySelectorAll('[data-nubs-seo]').forEach(node=>node.remove());
    document.head.append(metadata);
  }

  function dispatchShopifyPageView(main) {
    try {
      const event = new Event('shopify:page:view', {bubbles: true});
      Object.defineProperty(event, 'page', {
        configurable: true,
        value: {
          template: main?.dataset.template || (window.location.pathname.startsWith('/community/release/') ? 'metaobject' : 'page'),
          title: document.title,
          url: window.location.href,
        },
      });
      Object.defineProperty(event, 'detail', {configurable: true, value: {softNavigation: true}});
      document.dispatchEvent(event);
    } catch (error) {
      console.debug('[Nub soft navigation] Standard page-view event could not be dispatched.', error);
    }
  }

  function announcePageChange() {
    let announcer = document.querySelector('[data-nubs-soft-nav-announcer]');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.className = 'visually-hidden';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.dataset.nubsSoftNavAnnouncer = '';
      document.body.appendChild(announcer);
    }
    announcer.textContent = '';
    window.setTimeout(() => { announcer.textContent = `${document.title} loaded`; }, 20);
  }

  function updateHistory(url, mode, liveSearch) {
    const searchState = liveSearch ? {nubsLiveStoreSearch: true} : {};
    if (mode === 'push') {
      history.pushState({...searchState, nubsSoftNavigation: true, nubsUrl: url}, '', url);
    } else if (mode === 'replace') {
      history.replaceState({...history.state, ...searchState, nubsSoftNavigation: true, nubsUrl: url}, '', url);
    }
  }

  // The desktop Horizon layout scrolls .page-wrapper instead of window.
  // Saving window.scrollY as 0 was causing account/community navigation to
  // jump to the top and then apparently snap back after layout restoration.
  function pageScrollElement() {
    if (window.matchMedia('(min-width: 990px)').matches) {
      return document.querySelector('.page-wrapper') || document.scrollingElement || document.documentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  function saveCurrentScroll() {
    try {
      const scroller = pageScrollElement();
      history.replaceState({
        ...history.state,
        nubsSoftNavigation: true,
        nubsUrl: window.location.href,
        nubsScrollX: scroller.scrollLeft,
        nubsScrollY: scroller.scrollTop,
      }, '', window.location.href);
    } catch (_) {}
  }

  function restoreScroll(targetUrl, requestedScroll) {
    const url = new URL(targetUrl, window.location.href);
    if (url.hash) {
      let target = null;
      try { target = document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch (_) {}
      if (target) {
        target.scrollIntoView({block: 'start', behavior: 'instant'});
        return;
      }
    }
    const scroller = pageScrollElement();
    const y = requestedScroll && Number.isFinite(requestedScroll.y) ? Math.max(0, requestedScroll.y) : 0;
    const x = requestedScroll && Number.isFinite(requestedScroll.x) ? Math.max(0, requestedScroll.x) : 0;
    scroller.scrollTo({left: x, top: y, behavior: 'instant'});
  }

  // For account destinations, the Liquid response initially hides the entire
  // authenticated area. Reveal the locally cached view *before* swapping the
  // DOM so a fast return to Settings does not briefly render an empty page.
  // This is display-only: the account initializer still revalidates the user
  // through the authenticated bridge and controls all private operations.
  function prepareCachedAccountView(nextMain, targetUrl) {
    const account = nextMain.querySelector('[data-nubs-account-page]');
    if (!account || !window.NubsSessionState?.readProfile?.()?.authenticated) return;
    const signedIn = account.querySelector('[data-account-view="signed-in"]');
    if (!signedIn) return;
    signedIn.hidden = false;
    const params = new URL(targetUrl, window.location.href).searchParams;
    const allowed = ['profile','notifications','activity','approved','orders','lists','settings'];
    const requested = params.get('tab') || 'profile';
    const tab = allowed.includes(requested) ? requested : 'profile';
    account.querySelectorAll('[data-account-panel]').forEach(panel => {
      const active = panel.dataset.accountPanel === tab;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
    account.querySelectorAll('[data-account-tab]').forEach(button => {
      const active = button.dataset.accountTab === tab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-current', active ? 'page' : 'false');
    });
    if (tab === 'settings') {
      const setting = params.get('setting') || '';
      const allowedSettings = ['username','blocked','picture','visibility','public-lists','collection-coverage','notifications','delete-account'];
      const selected = allowedSettings.includes(setting) ? setting : '';
      const home = account.querySelector('[data-account-settings-home]');
      if (home) home.hidden = Boolean(selected);
      account.querySelectorAll('[data-account-setting-panel]').forEach(panel => {
        panel.hidden = panel.dataset.accountSettingPanel !== selected;
      });
    }
  }

  async function navigate(input, {historyMode = 'push', requestedScroll = null, liveSearch} = {}) {
    if (liveSearch?.signal.aborted) return false;
    const target = new URL(input, window.location.href);
    window.NubsCatalogClient?.prefetchPage?.(target);
    if (!isSoftNavigationUrl(target)) {
      window.location.assign(target.href);
      return;
    }

    const serial = ++navigationSerial;
    window.NubsStoreBrowseNavigation?.cancel({keepLiveSearch: Boolean(liveSearch)});
    activeNavigationController?.abort();
    const navigationController = new AbortController();
    activeNavigationController = navigationController;
    const {signal} = navigationController;
    const abort = () => navigationController.abort();
    liveSearch?.signal.addEventListener('abort', abort, {once: true});
    const currentMain = document.getElementById('MainContent');
    currentMain?.setAttribute('aria-busy', 'true');
    document.documentElement.classList.add('nubs-soft-nav-loading');

    if (historyMode === 'push') saveCurrentScroll();

    try {
      const savedView = takeShopView(target, historyMode);
      const result = savedView ? null : await fetchDocument(target.href, {signal});
      if (serial !== navigationSerial || signal.aborted) return false;

      const nextDocument = savedView?.document || new DOMParser().parseFromString(result.html, 'text/html');
      const nextMainSource = nextDocument.getElementById('MainContent');
      if (!nextMainSource) throw new Error('Destination did not contain MainContent.');

      const nextMain = document.importNode(nextMainSource, true);
      prepareCachedAccountView(nextMain, target.href);
      const nextExtrasSource = nextDocument.getElementById('NubsPageExtras');
      const nextExtras = nextExtrasSource ? document.importNode(nextExtrasSource, true) : null;
      const pageScripts = savedView?.scripts || [
        ...extractPageScripts(nextMain),
        ...extractPageScripts(nextExtras),
      ];
      const prewarmScripts = headPrewarmScripts(nextDocument);
      const essential = pageScripts.filter(script => !secondaryScripts.has(assetName(script.src)));
      const secondary = pageScripts.filter(script => secondaryScripts.has(assetName(script.src)));
      const destinationPath = localeNeutralPath(target.pathname);
      const hasStableLayout = Boolean(savedView) || /^\/products\//.test(destinationPath);

      const performSwap = () => {
        rememberShopView();
        setRouteStyles(nextDocument);
        currentRouteStyles = [...nextDocument.head.querySelectorAll('link[rel="stylesheet"][href]')]
          .map(link => link.cloneNode());
        currentPageScripts = pageScripts;
        document.dispatchEvent(new CustomEvent('nubs:beforepagechange', {
          detail: {from: window.location.href, to: target.href, soft: true},
        }));
        const liveMain = document.getElementById('MainContent');
        const liveExtras = document.getElementById('NubsPageExtras');
        // Pre-swap first-paint protection must work even when the source page
        // never loaded the homepage's dedicated early-head helper.
        document.documentElement.classList.toggle('nubs-new-releases-pending',
          /^\/(?:fr\/|es\/)?$/.test(target.pathname));
        syncDocumentMetadata(nextDocument, target.href);
        syncHeaderSurface(nextDocument);
        const restoreQuery = liveSearch && window.NubsStoreBrowseNavigation?.restoreQueryFocus(nextMain, liveSearch.input);
        liveMain?.replaceWith(nextMain);
        if (nextExtras && liveExtras) liveExtras.replaceWith(nextExtras);
        else if (liveExtras && !nextExtras) liveExtras.replaceChildren();
        else if (nextExtras && !liveExtras) nextMain.insertAdjacentElement('afterend', nextExtras);
        updateHistory(target.pathname + target.search + target.hash, historyMode, liveSearch);
        renderedUrl = window.location.href;
        // Synchronous restoration within the same task as DOM replacement.
        // The old route previously painted at a stale .page-wrapper scrollTop
        // while the new scripts loaded, then visibly jumped after settling.
        // Restore again after initialization only for genuine height changes.
        restoreScroll(target.href, savedView?.scroll || requestedScroll);
        restoreQuery?.();
      };

      // Keep the current, fully-laid-out page visible until the destination's
      // scripts are already in the browser cache. We intentionally avoid the
      // View Transition API here: its geometry interpolation can briefly squash
      // or stretch grids when two pages have very different dimensions.
      await Promise.all([
        ensureDestinationStyles(nextDocument),
        warmDestinationScripts(essential),
        window.NubsStoreBrowseNavigation?.prepareAvailability?.(nextMain.querySelector('[data-nubs-sold-out-pending]'), target, signal),
      ]);
      if (serial !== navigationSerial || signal.aborted) return false;

      // Once committed, the new route must finish initializing while the shopper types.
      liveSearch?.signal.removeEventListener('abort', abort);
      performSwap();

      await executeScripts(prewarmScripts, serial);
      await executeScripts(essential, serial);
      if (serial !== navigationSerial) return;
      if (!hasStableLayout) {
        if (document.querySelector('[data-nubs-collection-page]')) await window.NubsCollection?.whenReady?.();
        await settleLayout();
        if (serial !== navigationSerial) return;
        restoreScroll(target.href, requestedScroll);
      }
      const liveMain = document.getElementById('MainContent');
      liveMain?.removeAttribute('aria-busy');
      if (!liveSearch) liveMain?.focus({preventScroll: true});
      dispatchShopifyPageView(liveMain);
      announcePageChange();
      document.dispatchEvent(new CustomEvent('nubs:pagechange', {
        detail: {url: window.location.href, soft: true},
      }));
      if (secondary.length) {
        const start = () => executeScripts(secondary, serial).catch(error => {
          console.debug('[Nub soft navigation] Secondary content could not be loaded.', error);
        });
        // The first frame belongs to the destination's primary content.
        settleLayout().then(() => {
          if (serial === navigationSerial) start();
        });
      }
    } catch (error) {
      if (error?.name === 'AbortError' || serial !== navigationSerial || signal.aborted) return false;
      if (liveSearch?.automatic) return false;
      console.warn('[Nub soft navigation] Falling back to a normal page load.', error);
      window.location.assign(target.href);
    } finally {
      liveSearch?.signal.removeEventListener('abort', abort);
      if (serial === navigationSerial) {
        document.documentElement.classList.remove('nubs-soft-nav-loading');
        document.getElementById('MainContent')?.removeAttribute('aria-busy');
      }
    }
  }


  function navigateCollectionInPlace(target) {
    const current = new URL(window.location.href);
    if (localeNeutralPath(current.pathname) !== '/pages/my-collection' || localeNeutralPath(target.pathname) !== '/pages/my-collection') return false;
    if (!document.querySelector('[data-nubs-collection-page]') || !window.NubsCollection?.refresh) return false;

    saveCurrentScroll();
    history.pushState({}, '', `${target.pathname}${target.search}${target.hash}`);
    renderedUrl = window.location.href;
    Promise.resolve(window.NubsCollection.refresh()).then(()=>restoreScroll(target.href,null));
    document.dispatchEvent(new CustomEvent('nubs:pagechange', {
      detail: {url: window.location.href, soft: true, inPlace: true},
    }));
    return true;
  }

  document.addEventListener('nubs:session-cleared', () => { documentCache.clear(); shopViews.clear(); });
  document.addEventListener('nubs:cart-changed',()=>{
    shopViews.clear();
    for (const key of documentCache.keys()) if (/\/(products|collections)\//.test(new URL(key).pathname)) documentCache.delete(key);
  });
  document.addEventListener('submit',event=>{
    const form=event.target;
    if(event.defaultPrevented || !form.matches?.('#NubsCollectionFilterForm,#NubsSearchFilterForm') || String(form.method).toLowerCase()!=='get')return;
    const target=new URL(form.action,location.href);
    if(!isSoftNavigationUrl(target))return;
    target.search=new URLSearchParams(new FormData(form)).toString();
    event.preventDefault();navigate(target.href);
  });

  // Limit speculation to one deliberate product intent per displayed Shop page.
  let intentTimer;
  let warmedFrom;
  function scheduleProductWarm(anchor) {
    clearTimeout(intentTimer);
    if (!eligibleAnchor(anchor) || !/^\/products\//.test(localeNeutralPath(new URL(anchor.href).pathname))) return;
    intentTimer = setTimeout(() => {
      if (!anchor.isConnected || warmedFrom === renderedUrl ||
          document.readyState !== 'complete' || document.visibilityState !== 'visible' ||
          document.documentElement.classList.contains('nubs-soft-nav-loading') ||
          !document.querySelector('[data-nubs-search], [data-nubs-collection]') ||
          navigator.connection?.saveData ||
          ['slow-2g', '2g', '3g'].includes(navigator.connection?.effectiveType)) return;
      warmedFrom = renderedUrl;
      prefetch(anchor.href);
    }, 220);
  }
  document.addEventListener('pointerover', event => {
    const anchor = event.target.closest?.('a[href]');
    if (event.pointerType === 'mouse' && !anchor?.contains(event.relatedTarget)) scheduleProductWarm(anchor);
  });
  document.addEventListener('pointerout', event => {
    const anchor = event.target.closest?.('a[href]');
    if (anchor && !anchor.contains(event.relatedTarget)) clearTimeout(intentTimer);
  });
  document.addEventListener('focusin', event => scheduleProductWarm(event.target.closest?.('a[href]')));
  document.addEventListener('focusout', () => clearTimeout(intentTimer));

  document.addEventListener('click', (event) => {
    const anchor = event.target.closest?.('a[href]');
    if (!eligibleAnchor(anchor, event)) return;
    event.preventDefault();
    const target = new URL(anchor.href, window.location.href);
    if (navigateCollectionInPlace(target)) return;
    navigate(anchor.href).catch(() => window.location.assign(anchor.href));
  });

  function ownsStoreHistory(target, selector) {
    const path = localeNeutralPath(target.pathname);
    if (path !== localeNeutralPath(new URL(renderedUrl).pathname)) return false;
    if (selector === '[data-nubs-search]') return path === '/search' && Boolean(document.querySelector(selector));
    return /^\/collections\/[^/]+$/.test(path) && Boolean(document.querySelector('[data-nubs-collection]'));
  }

  function ownsHistoryScroll(target, state) {
    if (ownsStoreHistory(target, '[data-nubs-search]') ||
        ownsStoreHistory(target, '[data-nubs-collection]')) return false;

    // The Database controller owns its in-place filter and pagination history.
    return !(document.querySelector('[data-nubs-collection-page]') &&
      localeNeutralPath(target.pathname) === '/pages/my-collection' &&
      !state?.nubsSoftNavigationRouteChange);
  }

  window.addEventListener('popstate', (event) => {
    const target = new URL(window.location.href);

    if (!isSoftNavigationUrl(target.href)) {
      window.location.assign(target.href);
      return;
    }
    if (!ownsHistoryScroll(target, event.state)) {
      if (localeNeutralPath(target.pathname) === '/pages/my-collection') {
        renderedUrl = target.href;
      }
      return;
    }

    navigate(target.href, {
      historyMode: 'none',
      requestedScroll: {
        x: Number(event.state?.nubsScrollX || 0),
        y: Number(event.state?.nubsScrollY ?? event.state?.scrollTop ?? 0),
      },
    }).catch(() => window.location.assign(target.href));
  });

  // Browsers can restore the incoming entry's position before the outgoing DOM
  // is replaced. Keep native restoration disabled for this soft-navigation document.
  history.scrollRestoration = 'manual';

  // Mark the current entry without replacing any state another page controller needs.
  try {
    history.replaceState({...history.state, nubsSoftNavigation: true, nubsUrl: window.location.href}, '', window.location.href);
  } catch (_) {}

  window.NubsSoftNavigation = {
    initialized: true,
    navigate,
    prefetch,
    ownsStoreHistory,
    ownsHistoryScroll(target, state) {
      return isSoftNavigationUrl(target.href) && ownsHistoryScroll(target, state);
    },
    pageScrollElement,
    markStoreSection(url) { renderedUrl = url.href; },
    isEligible: isSoftNavigationUrl,
    get renderedUrl() { return renderedUrl; },
  };
})();
